import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { StatusBar, Style } from '@capacitor/status-bar';
import { getAppGlobalContext } from '@/utils/app-global-context';

export type AppearancePreference = 'system' | 'dark' | 'light';
export type EffectiveTheme = 'dark' | 'light';

export const APPEARANCE_PREF_KEY = 'appearance';
export const APPEARANCE_PICKER_SEEN_KEY = 'appearance_picker_seen';

const THEME_DARK_CLASS = 'liga-theme-dark';
const THEME_LIGHT_CLASS = 'liga-theme-light';

let mediaQuery: MediaQueryList | null = null;
let mediaListener: ((ev: MediaQueryListEvent) => void) | null = null;
let currentPreference: AppearancePreference = 'dark';
let currentEffective: EffectiveTheme = 'dark';

function normalizePreference(value: string | null | undefined): AppearancePreference {
  if (value === 'system' || value === 'light' || value === 'dark') {
    return value;
  }
  return 'dark';
}

export function resolveEffectiveTheme(mode: AppearancePreference = currentPreference): EffectiveTheme {
  if (mode === 'light') return 'light';
  if (mode === 'dark') return 'dark';
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark';
}

function setColorSchemeMeta(effective: EffectiveTheme): void {
  if (typeof document === 'undefined') return;
  let meta = document.querySelector('meta[name="color-scheme"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'color-scheme');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', effective);
  document.documentElement.style.colorScheme = effective;
}

/**
 * Переприменить StatusBar overlay/style.
 * Нужен после immersive splash и на resume: иначе env(safe-area-inset-top) часто 0.
 */
export async function syncNativeStatusBar(effective: EffectiveTheme = currentEffective): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    await StatusBar.show();
    // Overlay WebView → env(safe-area-inset-*) заполняется на Android/iOS.
    await StatusBar.setOverlaysWebView({ overlay: true });
    // Style.Dark = светлые иконки (тёмный статус-бар); Style.Light = тёмные иконки.
    await StatusBar.setStyle({ style: effective === 'light' ? Style.Light : Style.Dark });
  } catch {
    /* web / unsupported */
  }
}

function syncHtmlClass(effective: EffectiveTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle(THEME_DARK_CLASS, effective === 'dark');
  root.classList.toggle(THEME_LIGHT_CLASS, effective === 'light');
}

function detachSystemListener(): void {
  if (mediaQuery && mediaListener) {
    mediaQuery.removeEventListener('change', mediaListener);
  }
  mediaQuery = null;
  mediaListener = null;
}

function attachSystemListener(): void {
  detachSystemListener();
  if (typeof window === 'undefined' || !window.matchMedia) return;
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaListener = () => {
    if (currentPreference !== 'system') return;
    void applyAppearance('system');
  };
  mediaQuery.addEventListener('change', mediaListener);
}

export function getCurrentAppearancePreference(): AppearancePreference {
  return currentPreference;
}

export function getCurrentEffectiveTheme(): EffectiveTheme {
  return currentEffective;
}

export async function getAppearancePreference(): Promise<AppearancePreference> {
  const { value } = await Preferences.get({ key: APPEARANCE_PREF_KEY });
  currentPreference = normalizePreference(value);
  return currentPreference;
}

export async function setAppearancePreference(mode: AppearancePreference): Promise<void> {
  currentPreference = normalizePreference(mode);
  await Preferences.set({ key: APPEARANCE_PREF_KEY, value: currentPreference });
  await applyAppearance(currentPreference);
}

export async function applyAppearance(mode?: AppearancePreference): Promise<EffectiveTheme> {
  if (mode) {
    currentPreference = normalizePreference(mode);
  }
  const effective = resolveEffectiveTheme(currentPreference);
  currentEffective = effective;
  syncHtmlClass(effective);
  setColorSchemeMeta(effective);
  await syncNativeStatusBar(effective);

  if (currentPreference === 'system') {
    attachSystemListener();
  } else {
    detachSystemListener();
  }

  const ctx = getAppGlobalContext();
  if (ctx) {
    ctx.appearance = currentPreference;
    ctx.effectiveTheme = effective;
  }

  return effective;
}

export async function initAppearance(): Promise<EffectiveTheme> {
  await getAppearancePreference();
  return applyAppearance(currentPreference);
}

export async function isAppearancePickerSeen(): Promise<boolean> {
  const { value } = await Preferences.get({ key: APPEARANCE_PICKER_SEEN_KEY });
  return value === 'true';
}

export async function markAppearancePickerSeen(): Promise<void> {
  await Preferences.set({ key: APPEARANCE_PICKER_SEEN_KEY, value: 'true' });
}
